import React, { useEffect, useMemo, useState } from "react";
import { api, formatTRY } from "@/lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Package as PackageIcon, Zap, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { CAR_PARTS, SERVICES, COLORS } from "./Parts";

const emptyForm = () => ({ name: "", description: "", items: [] });

export default function Packages() {
  const [items, setItems] = useState([]);
  const [parts, setParts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [deleteId, setDeleteId] = useState(null);

  // Sihirbaz state — pakete kalem eklerken
  const [wizOpen, setWizOpen] = useState(false);
  const [wizStep, setWizStep] = useState(1);
  const [wizPart, setWizPart] = useState("");
  const [wizService, setWizService] = useState(null);
  const [wizColor, setWizColor] = useState("");
  const [wizPrice, setWizPrice] = useState("");
  const [wizQty, setWizQty] = useState(1);

  const load = async () => {
    const [pk, pt] = await Promise.all([api.get("/packages"), api.get("/parts")]);
    setItems(pk.data);
    setParts(pt.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description || "", items: p.items || [] });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Paket adı gerekli"); return; }
    if (form.items.length === 0) { toast.error("En az bir kalem ekleyin"); return; }
    const payload = {
      name: form.name.trim(),
      description: form.description,
      items: form.items.map((it) => ({
        name: it.name,
        category: it.category || "",
        price: Number(it.price) || 0,
        quantity: Number(it.quantity) || 1,
      })),
    };
    if (editing) { await api.put(`/packages/${editing.id}`, payload); toast.success("Paket güncellendi"); }
    else { await api.post("/packages", payload); toast.success("Paket oluşturuldu"); }
    setOpen(false); load();
  };

  const del = async () => { await api.delete(`/packages/${deleteId}`); toast.success("Silindi"); setDeleteId(null); load(); };

  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, patch) =>
    setForm({ ...form, items: form.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });

  const totalOfForm = useMemo(
    () => form.items.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 1), 0),
    [form.items]
  );

  // Sihirbaz
  const resetWizard = () => {
    setWizStep(1); setWizPart(""); setWizService(null); setWizColor("");
    setWizPrice(""); setWizQty(1);
  };
  const openWizard = () => { resetWizard(); setWizOpen(true); };
  const wizResolved = useMemo(() => {
    if (!wizService || !wizPart) return null;
    if (wizService.requiresColor && !wizColor) return null;
    const { name, category } = wizService.resolve(wizPart, wizColor);
    const existing = parts.find((x) => x.name === name && x.category === category);
    return { name, category, existing };
  }, [wizService, wizPart, wizColor, parts]);

  useEffect(() => {
    if (wizStep === 4 && wizResolved) {
      if (wizResolved.existing) setWizPrice(String(wizResolved.existing.price));
    }
  }, [wizStep, wizResolved]);

  const wizCanNext = () => {
    if (wizStep === 1) return !!wizPart;
    if (wizStep === 2) return !!wizService;
    if (wizStep === 3) return !!wizColor;
    return false;
  };
  const wizGoNext = () => {
    if (wizStep === 1 && wizPart) setWizStep(2);
    else if (wizStep === 2 && wizService) {
      if (!wizService.parts.includes(wizPart)) setWizPart(wizService.parts[0] || "");
      setWizStep(wizService.requiresColor ? 3 : 4);
    } else if (wizStep === 3 && wizColor) setWizStep(4);
  };

  const wizAddToPackage = () => {
    if (!wizResolved) return;
    const price = Number(wizPrice) || 0;
    const qty = Number(wizQty) || 1;
    setForm({
      ...form,
      items: [
        ...form.items,
        { name: wizResolved.name, category: wizResolved.category, price, quantity: qty },
      ],
    });
    toast.success(`${wizResolved.name} pakete eklendi`);
    setWizOpen(false);
    resetWizard();
  };

  return (
    <div data-testid="packages-page" className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <PackageIcon className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-blue-900 font-semibold">Hizmet Paketleri</p>
          <p className="text-xs text-blue-800 mt-1">
            Sıkça kullanılan işlem kombinasyonlarını paket olarak tanımlayın. Teklif oluştururken tek tıkla paket
            içindeki tüm kalemleri ekleyebilirsiniz.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">{items.length} Paket</h2>
          <p className="text-sm text-slate-500">Örn: Küçük Hasar Onarımı, Komple Boya + Cila</p>
        </div>
        <Button data-testid="add-package-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" /> Yeni Paket
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
          <PackageIcon className="w-10 h-10 text-slate-400 mx-auto mb-2" />
          <p className="text-slate-700 font-semibold">Henüz paket yok.</p>
          <p className="text-slate-500 text-sm mt-1">"Yeni Paket" ile ilk paketinizi oluşturun.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`package-card-${p.id}`}
              className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-slate-900">{p.name}</p>
                  {p.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{p.description}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <Button data-testid={`edit-package-${p.id}`} variant="ghost" size="icon" onClick={() => openEdit(p)} className="h-8 w-8">
                    <Pencil className="w-3.5 h-3.5 text-slate-600" />
                  </Button>
                  <Button data-testid={`delete-package-${p.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(p.id)} className="h-8 w-8 hover:bg-rose-50">
                    <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                  </Button>
                </div>
              </div>
              <div className="mt-3 space-y-1">
                {(p.items || []).map((it, i) => (
                  <div key={`${p.id}-${i}-${it.name}`} className="flex justify-between text-xs">
                    <span className="text-slate-700 truncate mr-2">
                      {it.name}
                      {it.quantity > 1 && <span className="text-slate-500"> × {it.quantity}</span>}
                    </span>
                    <span className="font-mono-num text-slate-700 shrink-0">{formatTRY((it.price || 0) * (it.quantity || 1))}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between items-center">
                <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                  {p.item_count} kalem
                </span>
                <span className="font-display font-bold text-slate-900 font-mono-num text-lg">
                  {formatTRY(p.total_price || 0)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Paket Diyaloğu */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="package-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Paketi Düzenle" : "Yeni Paket"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label>Paket Adı</Label>
                <Input
                  data-testid="package-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Örn: Küçük Hasar Onarımı"
                />
              </div>
              <div>
                <Label>Açıklama (opsiyonel)</Label>
                <Textarea
                  data-testid="package-desc-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Paketin kapsamını kısaca açıklayın"
                  rows={2}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Paket Kalemleri</Label>
                <Button
                  type="button"
                  data-testid="package-add-item-btn"
                  variant="outline"
                  size="sm"
                  onClick={openWizard}
                  className="border-amber-500 text-amber-700 hover:bg-amber-50"
                >
                  <Zap className="w-3.5 h-3.5 mr-1" /> Sihirbaz ile Ekle
                </Button>
              </div>
              {form.items.length === 0 ? (
                <div className="border-2 border-dashed border-slate-200 rounded-md p-6 text-center text-sm text-slate-500">
                  Henüz kalem yok. "Sihirbaz ile Ekle" butonuyla adım adım ekleyin.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Kalem</th>
                        <th className="text-right px-3 py-2 font-semibold w-20">Adet</th>
                        <th className="text-right px-3 py-2 font-semibold w-28">Birim ₺</th>
                        <th className="text-right px-3 py-2 font-semibold w-28">Ara Toplam</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((it, i) => (
                        <tr key={`${it.name}-${it.category}-${i}`} className="border-t border-slate-100">
                          <td className="px-3 py-2">
                            <div className="font-semibold text-slate-900 text-sm">{it.name}</div>
                            <div className="text-[10px] text-slate-500">{it.category}</div>
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              data-testid={`pkg-item-qty-${i}`}
                              type="number" step="1" min="1"
                              value={it.quantity}
                              onChange={(e) => updateItem(i, { quantity: e.target.value })}
                              className="h-8 text-xs font-mono-num text-right"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              data-testid={`pkg-item-price-${i}`}
                              type="number" step="0.01"
                              value={it.price}
                              onChange={(e) => updateItem(i, { price: e.target.value })}
                              className="h-8 text-xs font-mono-num text-right"
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-mono-num text-sm font-semibold text-slate-900">
                            {formatTRY((Number(it.price) || 0) * (Number(it.quantity) || 1))}
                          </td>
                          <td className="px-2 py-2">
                            <Button
                              type="button" variant="ghost" size="icon"
                              onClick={() => removeItem(i)}
                              className="h-7 w-7 hover:bg-rose-50"
                              data-testid={`pkg-item-remove-${i}`}
                            >
                              <X className="w-3.5 h-3.5 text-rose-600" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50">
                      <tr>
                        <td colSpan="3" className="px-3 py-2 text-right text-xs uppercase tracking-widest text-slate-600 font-semibold">
                          Paket Toplamı
                        </td>
                        <td className="px-3 py-2 text-right font-mono-num text-base font-bold text-slate-900" data-testid="pkg-total">
                          {formatTRY(totalOfForm)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="save-package-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800">
              {editing ? "Güncelle" : "Paketi Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sihirbaz Diyaloğu */}
      <Dialog open={wizOpen} onOpenChange={(v) => { setWizOpen(v); if (!v) resetWizard(); }}>
        <DialogContent data-testid="pkg-wiz-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" /> Sihirbaz ile Kalem Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center gap-2 py-2">
            {[1, 2, 3, 4].map((s) => {
              if (s === 3 && wizService && !wizService.requiresColor) return null;
              const active = wizStep === s;
              const done = wizStep > s;
              return (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center ${
                    done ? "bg-emerald-500 text-white" : active ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"
                  }`}>
                    {done ? <CheckCircle2 className="w-4 h-4" /> : s === 4 ? "✓" : s}
                  </div>
                  {s < 4 && <div className={`w-8 h-0.5 ${done ? "bg-emerald-500" : "bg-slate-200"}`} />}
                </div>
              );
            })}
          </div>

          <div className="space-y-4 py-2">
            {wizStep === 1 && (
              <div>
                <Label className="text-sm text-slate-700 font-semibold">1. Adım · Aracın Parçasını Seçin</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                  {CAR_PARTS.map((p) => (
                    <button
                      key={p} type="button"
                      data-testid={`pkg-wiz-part-${p}`}
                      onClick={() => setWizPart(p)}
                      className={`px-3 py-2.5 rounded-md text-xs font-semibold border transition-all ${
                        wizPart === p
                          ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {wizStep === 2 && (
              <div>
                <Label className="text-sm text-slate-700 font-semibold">2. Adım · Yapılacak İşlem Alanı</Label>
                <p className="text-xs text-slate-500 mb-3">Parça: <b>{wizPart}</b></p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {SERVICES.filter((s) => s.parts.includes(wizPart) || s.key === "sasi").map((s) => {
                    if (s.key === "komple-boya") return null;
                    if (s.key === "sasi" && wizPart !== "Şasi Ucu") return null;
                    const Icon = s.icon;
                    const active = wizService?.key === s.key;
                    return (
                      <button
                        key={s.key} type="button"
                        data-testid={`pkg-wiz-service-${s.key}`}
                        onClick={() => setWizService(s)}
                        className={`px-3 py-3 rounded-md text-sm font-semibold border transition-all flex items-center gap-2 ${
                          active
                            ? `bg-gradient-to-br ${s.color} text-white border-transparent shadow-sm`
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                        }`}
                      >
                        <Icon className="w-4 h-4" /> {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {wizStep === 3 && wizService?.requiresColor && (
              <div>
                <Label className="text-sm text-slate-700 font-semibold">3. Adım · Boya Türünü Seçin</Label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {COLORS.map((c) => (
                    <button
                      key={c} type="button"
                      data-testid={`pkg-wiz-color-${c}`}
                      onClick={() => setWizColor(c)}
                      className={`px-4 py-4 rounded-md text-sm font-semibold border transition-all ${
                        wizColor === c
                          ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {wizStep === 4 && wizResolved && (
              <div className="space-y-4">
                <Label className="text-sm text-slate-700 font-semibold">Son Adım · Onay ve Adet</Label>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Kalem:</span>
                    <span className="font-semibold text-slate-900">{wizResolved.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Kategori:</span>
                    <span className="font-semibold text-slate-900">{wizResolved.category}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Birim Fiyat (₺)</Label>
                    <Input
                      data-testid="pkg-wiz-price-input"
                      type="number" step="0.01"
                      value={wizPrice}
                      onChange={(e) => setWizPrice(e.target.value)}
                      className="font-mono-num h-11" autoFocus
                    />
                  </div>
                  <div>
                    <Label>Adet</Label>
                    <Input
                      data-testid="pkg-wiz-qty-input"
                      type="number" step="1" min="1"
                      value={wizQty}
                      onChange={(e) => setWizQty(e.target.value)}
                      className="font-mono-num h-11"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-row justify-between gap-2">
            <div>
              {wizStep > 1 && (
                <Button variant="outline" onClick={() => setWizStep((s) => {
                  if (s === 4 && wizService && !wizService.requiresColor) return 2;
                  return s - 1;
                })}>
                  ← Geri
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setWizOpen(false); resetWizard(); }}>İptal</Button>
              {wizStep < 4 ? (
                <Button
                  data-testid="pkg-wiz-next-btn"
                  onClick={wizGoNext} disabled={!wizCanNext()}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  İleri →
                </Button>
              ) : (
                <Button
                  data-testid="pkg-wiz-add-btn"
                  onClick={wizAddToPackage}
                  disabled={!wizPrice || Number(wizPrice) <= 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Pakete Ekle
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Paket silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-package" onClick={del} className="bg-rose-600 hover:bg-rose-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
